import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Search, ShoppingCart, Calendar, Tag as TagIcon } from 'lucide-react';
import LinkSimpleIcon from '@revfluence/fresh-icons/regular/esm/LinkSimpleIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faLink, faPlus, faRefresh, faTags } from '@fortawesome/free-solid-svg-icons';
import { useApolloClient } from '@frontend/applications/AffiliatesApp/hooks';
import { GET_OFFERS_QUERY, GET_OFFER_BY_ID_WITH_STATS_QUERY } from '@frontend/applications/AffiliatesApp/queries';
import { GetOffers_offers } from '@frontend/applications/AffiliatesApp/queries/types/GetOffers';
import { GetofferByIdWithStats, GetofferByIdWithStatsVariables } from '@frontend/applications/AffiliatesApp/queries/types/GetofferByIdWithStats';
import { getPayoutLabel, getUniqueRefreshTagColor } from '@frontend/applications/AffiliatesApp/components/OfferForm/utils/OfferFormUtils';
import { logger } from '@common';
import { Loader2 } from 'lucide-react';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { AutomationActionNodeSubType, AutomationNodeType } from '@frontend/app/types/globalTypes';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { TAutomationActionOption, MessageTemplate, MessageTemplateInput } from '@frontend/app/hooks';
import { OFFER_STATUS, OFFER_TYPE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { AutomationNodeV2 } from '../../types';
import { Button } from '@/shadcn/components/ui/button';
import { Label } from '@/shadcn/components/ui/label';
import { Card, CardContent } from '@/shadcn/components/ui/card';
import { Separator } from '@/shadcn/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shadcn/components/ui/accordion';
import { getOfferDiscountAmount, getOfferIconData } from '../../utils/automationTreeUtils';
import { SendEmailNotificationForm } from './components/SendEmailNotificationForm';

interface AddToOfferActionFormProps {
  selectedNode: AutomationNodeV2;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  actionOptions?: TAutomationActionOption[];
  onUpdateMessageTemplate?: (updatedTemplate: MessageTemplate | MessageTemplateInput) => void;
  resetMessageTemplate?: boolean;
  onSave: () => void;
}

export const AddToOfferActionForm: React.FC<AddToOfferActionFormProps> = ({
  selectedNode,
  onUpdateNode,
  actionOptions = [],
  onUpdateMessageTemplate,
  resetMessageTemplate = false,
  onSave,
}) => {
  const [selectedOfferId, setSelectedOfferId] = useState<number | null>(null);
  const [selectedPayoutId, setSelectedPayoutId] = useState<number | null>(null);
  const [isAutomatedPayout, setIsAutomatedPayout] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');
  const [emailValidationError, setEmailValidationError] = useState<string>('');
  const [emailValidationErrors, setEmailValidationErrors] = useState<string[]>([]);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [offers, setOffers] = useState<GetOffers_offers[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);

  // Selected offer details
  const [selectedOfferDetails, setSelectedOfferDetails] = useState<GetofferByIdWithStats['offer'] | null>(null);

  // Reset payout selection when offer changes
  useEffect(() => {
    if (selectedOfferId !== selectedNode.metadata.offerId) {
      setSelectedPayoutId(null);
      setIsAutomatedPayout(false);
    }
  }, [selectedOfferId, selectedNode.metadata.offerId]);

  // Use STA Apollo client to fetch offers
  const staApolloClient = useApolloClient();

  // Fetch offers on component mount
  useEffect(() => {
    if (!staApolloClient) return;

    setLoading(true);
    staApolloClient.query({
      query: GET_OFFERS_QUERY,
      fetchPolicy: 'network-only',
    }).then((data) => {
      setOffers(data.data.offers || []);
      setError(null);
    }).catch((err) => {
      logger.error('Error fetching offers:', err);
      setError('Failed to load offers');
      setOffers([]);
    }).finally(() => {
      setLoading(false);
    });
  }, [staApolloClient]);

  // Initialize state from selectedNode metadata - only when node ID changes
  useEffect(() => {
    if (selectedNode && selectedNode.type === AutomationNodeType.ACTION) {
      setSelectedOfferId(selectedNode.metadata.offerId || null);
      setSelectedPayoutId(selectedNode.metadata.selectedPayoutId || null);
      setIsAutomatedPayout(selectedNode.metadata.isAutomatedPayout || false);
      setSendEmail(selectedNode.metadata.sendEmail || false);
    }
  }, [selectedNode?.id, selectedNode]);

  // Fetch offer details when selectedOfferId changes
  useEffect(() => {
    if (selectedOfferId && staApolloClient) {
      staApolloClient.query<GetofferByIdWithStats, GetofferByIdWithStatsVariables>({
        query: GET_OFFER_BY_ID_WITH_STATS_QUERY,
        variables: {
          id: selectedOfferId,
        },
        fetchPolicy: 'network-only',
      }).then((response) => {
        setSelectedOfferDetails(response.data.offer);
      }).catch((err) => {
        logger.error('Error fetching offer details:', err);
        setSelectedOfferDetails(null);
      });
    } else {
      setSelectedOfferDetails(null);
    }
  }, [selectedOfferId, staApolloClient]);

  // Get selected offer details from the list (for basic info)
  const selectedOffer = useMemo(() => {
    return offers.find((offer) => offer.id === selectedOfferId) || null;
  }, [offers, selectedOfferId]);

  // Check if automated payouts are active for the selected offer
  const automatedPayoutsActive = useMemo(() => {
    return selectedOfferDetails?.offer.offerAutomationConfig?.isActive === true;
  }, [selectedOfferDetails]);

  // Get available payouts from the selected offer details with filtering
  const availablePayouts = useMemo(() => {
    if (!selectedOfferDetails?.offer.payouts) return [];

    const allPayouts = selectedOfferDetails.offer.payouts;

    // If automated payouts are not active, show all payouts
    if (!automatedPayoutsActive) {
      return allPayouts;
    }

    // If automated payouts are active, filter out payouts tied to active automation configs
    return allPayouts.filter((payout) => {
      // If payout has no automation config, include it
      if (!payout.offerAutomationConfigId) {
        return true;
      }

      // If payout has automation config, check if it's active
      // Since we only have the config ID, we need to check if the current offer's automation config
      // matches this payout's config and if it's active
      const isPayoutAutomationActive = selectedOfferDetails.offer.offerAutomationConfig?.id === payout.offerAutomationConfigId
        && selectedOfferDetails.offer.offerAutomationConfig?.isActive === true;

      // Exclude if the automation config is active
      return !isPayoutAutomationActive;
    });
  }, [selectedOfferDetails, automatedPayoutsActive]);

  // Check if automated payout is available for the selected offer
  const isAutomatedPayoutAvailable = useMemo(() => {
    return automatedPayoutsActive;
  }, [automatedPayoutsActive]);

  const getOfferActiveDate = (offerDetails: GetofferByIdWithStats['offer']) => {
    if (!offerDetails) return 'Unknown date';

    // Check promo start date for promo code offers
    if (offerDetails.offer.promos && offerDetails.offer.promos.length > 0) {
      const promo = offerDetails.offer.promos[0];
      if (promo.startDate) {
        return `Active from ${new Date(promo.startDate).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })}`;
      }
    }

    // For link offers or any offer, use creation date
    if (offerDetails.offer.createdDate) {
      return `Created on ${new Date(offerDetails.offer.createdDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })}`;
    }

    return 'Unknown date';
  };

  const getCommissionDetails = (offerDetails: GetofferByIdWithStats['offer']) => {
    if (!offerDetails?.offer.payouts || offerDetails.offer.payouts.length === 0) return [];

    return offerDetails.offer.payouts.map((payout) => {
      return getPayoutLabel(payout);
    }).filter(Boolean);
  };

  const getOfferDetailsContent = (offerDetails: GetofferByIdWithStats['offer']) => {
    if (!offerDetails) return [];

    const details = [];
    const offer = offerDetails.offer;

    // For link offers
    if (offer.type === OFFER_TYPE.LINK) {
      // Check expiration date
      if (offer.expirationDate) {
        details.push(`Offer ends on ${new Date(offer.expirationDate).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })}`);
      }

      // Check if creators can create custom deeplinks
      if (offer.links && offer.links.length > 0) {
        const link = offer.links[0];
        if (link.creatorDeeplink) {
          details.push('Creators are allowed to create custom deeplinks.');
        }
      }

      return details;
    }

    // For promo code offers
    if (offer.promos && offer.promos.length > 0) {
      const promo = offer.promos[0];

      // Purchase type
      if (promo.isOneTime && promo.isSubscription) {
        details.push('Applies to subscription and one-time purchase');
      } else if (promo.isOneTime) {
        details.push('Applies to one-time purchase');
      } else if (promo.isSubscription) {
        details.push('Applies to subscription purchase');
      }

      // Usage limits
      if (promo.usageLimitRule === 'NONE') {
        details.push('No minimum purchase requirements');
      } else if (promo.usageLimitRule === 'MINIMUM_PURCHASE_AMOUNT' && promo.usageLimitAmount) {
        details.push(`Minimum purchase of $${promo.usageLimitAmount}`);
      }

      // Usage limits
      if (promo.specialLimitOnePerSale) {
        details.push('One use per order');
      }
      if (!promo.specialLimitUsageCapEnabled) {
        details.push('No usage limits');
      }

      // Discount combinations
      if (promo.providerMetadata?.discountCombination) {
        const combo = promo.providerMetadata.discountCombination;
        if (!combo.orderDiscounts && !combo.productDiscounts && !combo.shippingDiscounts) {
          details.push("Can't combine it with other offers");
        }
      }
    }

    return details;
  };

  // Filter offers based on search term and exclude expired offers
  const filteredOffers = useMemo(() => {
    // First filter out expired offers
    const activeOffers = offers.filter((offer) => !offer.expired);

    // Then apply search filter if search term exists
    if (!searchTerm.trim()) return activeOffers;
    return activeOffers.filter((offer) =>
      offer.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [offers, searchTerm]);

  const handleOfferSelect = (offerId: number, e?: React.MouseEvent) => {
    e?.stopPropagation(); // Prevent event from bubbling up
    setSelectedOfferId(offerId);
    setIsDropdownOpen(false);
  };

  const handlePayoutSelect = (value: string) => {
    if (value === 'automated') {
      setIsAutomatedPayout(true);
      setSelectedPayoutId(null);
    } else {
      setIsAutomatedPayout(false);
      setSelectedPayoutId(value ? parseInt(value, 10) : null);
    }
  };

  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = 400; // Approximate max height of dropdown

      // Check if there's enough space below, otherwise position above
      const spaceBelow = viewportHeight - rect.bottom;
      const positionAbove = spaceBelow < dropdownMaxHeight && rect.top > dropdownMaxHeight;

      setDropdownPosition({
        top: positionAbove
          ? rect.top + window.scrollY - dropdownMaxHeight - 4
          : rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  const handleDropdownToggle = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!isDropdownOpen) {
      updateDropdownPosition();
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside - improved event handling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!isDropdownOpen) return;

      const target = event.target as HTMLElement;

      // Check if click is inside dropdown container or portal
      const isInsideDropdown = target.closest('.offer-dropdown-container');
      const isInsideDropdownPortal = target.closest('.offer-dropdown-portal');
      const isTooltip = target.closest('[role="tooltip"]');
      const isInput = target.tagName.toLowerCase() === 'input';

      // Don't close if clicking inside dropdown, tooltip, or on input elements
      if (isInsideDropdown || isInsideDropdownPortal || isTooltip || isInput) {
        return;
      }

      // Close dropdown
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      // Add a small delay to prevent immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true);
        document.addEventListener('touchstart', handleClickOutside, true);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
        document.removeEventListener('touchstart', handleClickOutside, true);
      };
    }
  }, [isDropdownOpen]);

  // Handle window resize to reposition dropdown
  useEffect(() => {
    const handleResize = () => {
      if (isDropdownOpen) {
        updateDropdownPosition();
      }
    };

    if (isDropdownOpen) {
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize, true);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [isDropdownOpen]);

  const validateSelection = () => {
    const noOfferError = 'Please select an offer to add members to';
    const noPayoutError = 'Please select a payout';

    let error = '';
    let emailError = '';

    if (!selectedOfferId) {
      error = noOfferError;
    } else if (!isAutomatedPayout && !selectedPayoutId) {
      error = noPayoutError;
    }

    setValidationError(error);

    // Check email validation if email is enabled
    if (sendEmail && !isEmailValid) {
      if (emailValidationErrors.length > 0) {
        emailError = emailValidationErrors[0];
      } else {
        emailError = 'Please complete email configuration';
      }
      setEmailValidationError(emailError);
    } else {
      setEmailValidationError('');
    }

    return error === '' && emailError === '';
  };

  // Helper function to check if current selections are valid (without setting error)
  const isCurrentSelectionValid = useCallback(() => {
    if (!selectedOfferId) return false;
    if (!isAutomatedPayout && !selectedPayoutId) return false;
    return true;
  }, [selectedOfferId, isAutomatedPayout, selectedPayoutId]);

  // Clear validation errors when selections become valid
  useEffect(() => {
    // Only clear errors if there's currently an error and the selection is now valid
    if (validationError && isCurrentSelectionValid()) {
      setValidationError('');
    }
  }, [selectedOfferId, selectedPayoutId, isAutomatedPayout, validationError, isCurrentSelectionValid]);

  const handleSave = () => {
    if (!validateSelection()) return;

    // Prepare metadata object with proper typing
    const metadata: any = {
      ...selectedNode.metadata,
      offerId: selectedOfferId,
      sendEmail,
    };

    // Handle payout selection logic
    if (isAutomatedPayout) {
      metadata.isAutomatedPayout = true;
      // Remove selectedPayoutId if automated payout is selected
      delete metadata.selectedPayoutId;
    } else {
      metadata.selectedPayoutId = selectedPayoutId;
      metadata.isAutomatedPayout = false;
    }

    // Update the node metadata with the form values
    onUpdateNode(selectedNode.id, {
      metadata,
    });

    onSave();
  };

  const updateSendEmailState = (value: boolean) => {
    setSendEmail(value);
    // Update node metadata immediately but preserve offer selection
    onUpdateNode(selectedNode.id, {
      metadata: {
        ...selectedNode.metadata,
        sendEmail: value,
        // Preserve current local state for offer selection
        offerId: selectedOfferId || selectedNode.metadata.offerId,
        selectedPayoutId: selectedPayoutId || selectedNode.metadata.selectedPayoutId,
        isAutomatedPayout,
      },
    });
  };

  const handleEmailMetadataUpdate = (updates: any) => {
    // Handle template and resource metadata updates from SendEmailNotificationForm
    // Preserve current local state for offer selection
    onUpdateNode(selectedNode.id, {
      metadata: {
        ...selectedNode.metadata,
        ...updates,
        // Preserve current local state for offer selection
        offerId: selectedOfferId || selectedNode.metadata.offerId,
        selectedPayoutId: selectedPayoutId || selectedNode.metadata.selectedPayoutId,
        isAutomatedPayout,
      },
    });
  };

  const handleEmailValidationChange = useCallback((isValid: boolean, errors: string[]) => {
    setIsEmailValid(isValid);
    setEmailValidationErrors(errors);
    // Clear email error when validation passes
    if (isValid) {
      setEmailValidationError('');
    }
  }, []);

  // Combine ADD_TO_OFFER and SEND_EMAIL templates and resources for email form
  const combinedActionOptions = useMemo(() => {
    const addToOfferOption = actionOptions.find((option) => option.type === AutomationActionNodeSubType.ADD_TO_OFFER);
    const sendEmailOption = actionOptions.find((option) => option.type === AutomationActionNodeSubType.SEND_EMAIL);

    if (!addToOfferOption && !sendEmailOption) return [];

    // Create a combined option with resources from ADD_TO_OFFER and templates from both
    const combinedOption = {
      ...(addToOfferOption || {}),
      type: AutomationActionNodeSubType.ADD_TO_OFFER,
      emailResources: addToOfferOption?.emailResources || [],
      messageTemplates: [
        ...(addToOfferOption?.messageTemplates || []),
        ...(sendEmailOption?.messageTemplates || [])
      ]
    } as any;

    return [combinedOption];
  }, [actionOptions]);

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-grey-5">Loading offers...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-red-500 mb-2">{error}</p>
            <p className="text-xs text-grey-5">Please try again later</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden p-1">
      {/* Scrollable content area with bottom padding for buttons */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pb-40">
        {/* Offer selection section */}
        <div className="space-y-3">
          <div className="relative self-stretch mt-[-1.00px] font-medium text-grey-6 text-sm">
            Select Offer to add member to
          </div>

          <div className="relative offer-dropdown-container">
            {/* Main selector button */}
            <div ref={buttonRef}>
              {selectedOffer ? (
                <Card className="p-3 bg-gray-1 rounded-2xl border border-solid border-gray-2 relative">
                  <CardContent className="p-0">
                    <div className="flex flex-col items-start gap-8">
                      <div className="flex flex-col items-start gap-2 relative self-stretch w-full flex-[0_0_auto]">
                        <div className="flex-col items-start gap-4 flex relative self-stretch w-full flex-[0_0_auto]">
                          <div className="items-center gap-2 flex relative self-stretch w-full flex-[0_0_auto]">
                            <div className={`relative w-[60px] h-[60px] rounded-lg ${getOfferIconData(selectedOffer).bgColor} flex items-center justify-center`}>
                              <div className="w-12 h-12 flex items-center justify-center">
                                {getOfferIconData(selectedOffer).iconType === 'link' ? (
                                  <FontAwesomeIcon icon={faLink} className="w-8 h-8 text-white" />
                                ) : (
                                  <FontAwesomeIcon icon={faTags} className="w-8 h-8 text-white" />
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-start relative flex-1 grow">
                              <div className="items-center justify-between flex relative self-stretch w-full flex-[0_0_auto]">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="relative w-fit text-grey-6 text-xs leading-5 ">
                                        {selectedOffer.name.length > 15 ? `${selectedOffer.name.substring(0, 15)}...` : selectedOffer.name}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[300px]">
                                      {selectedOffer.name}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <H3 className="text-grey-6 m-0 text-lg font-medium leading-8">
                                {selectedOfferDetails ? getOfferDiscountAmount(selectedOfferDetails) : 'Offer Details'}
                              </H3>
                            </div>
                          </div>
                        </div>
                        <div className="items-center gap-[5px] flex relative self-stretch w-full flex-[0_0_auto]">

                          <div className="flex w-[18px] h-[18px] items-center justify-center relative">
                            <Calendar className="w-4 h-4 text-green-4" />
                          </div>
                          <div className="relative w-fit mt-[-1.00px] font-medium-75-labels font-[number:var(--medium-75-labels-font-weight)] text-grey-5 text-[length:var(--medium-75-labels-font-size)] tracking-[var(--medium-75-labels-letter-spacing)] leading-[var(--medium-75-labels-line-height)] whitespace-nowrap [font-style:var(--medium-75-labels-font-style)]">
                            {selectedOfferDetails ? getOfferActiveDate(selectedOfferDetails) : 'Unknown date'}
                          </div>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="relative flex-[0_0_auto]">
                                  <FontAwesomeIcon icon={faInfoCircle} className="w-3 h-3 text-grey-3" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{selectedOfferDetails ? getOfferActiveDate(selectedOfferDetails) : 'Unknown date'}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                        </div>
                        <Separator className="relative self-stretch w-full h-0.5" />
                        <Accordion type="single" collapsible className="w-full">
                          <AccordionItem value="offer-details" className="border-none">
                            <AccordionTrigger className="items-center gap-[5px] flex relative self-stretch w-full flex-[0_0_auto] hover:no-underline py-0">
                              <div className="relative w-fit text-xs leading-5 font-medium text-grey-6">
                                Offer Details
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="pt-3 pb-0">
                              <div className="space-y-3">
                                {/* Commissions Section */}
                                {selectedOfferDetails && getCommissionDetails(selectedOfferDetails).length > 0 && (
                                  <div>
                                    <p className="text-grey-5 text-xs font-medium mb-2">Commissions</p>
                                    <div className="flex gap-2 flex-wrap">
                                      {getCommissionDetails(selectedOfferDetails).map((commission, index) => {
                                        return (
                                          <div
                                            key={index}
                                            className={`${getUniqueRefreshTagColor(index)} text-grey-0 rounded-lg h-[22px] px-2 w-max flex items-center`}
                                          >
                                            {commission}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {/* Creator Benefits */}
                                {selectedOfferDetails && (
                                  <div className="space-y-2">
                                    {selectedOfferDetails.offer.isPromoLink && (
                                      <div className="flex items-center gap-2">
                                        <LinkSimpleIcon className="w-4 h-4 text-magenta-5" />
                                        <p className="text-grey-5 text-xs font-medium m-0">Creators will receive a shareable link</p>
                                      </div>
                                    )}
                                    {selectedOfferDetails.offer.isLandingPageEnabled && (
                                      <div className="flex items-center gap-2">
                                        <TagIcon className="w-4 h-4 text-blue-5" />
                                        <p className="text-grey-5 text-xs font-medium m-0">Personalized CreatorStore</p>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Detailed Offer Information */}
                                {selectedOfferDetails && getOfferDetailsContent(selectedOfferDetails).length > 0 && (
                                  <div>
                                    <p className="text-grey-5 text-xs font-medium mb-2">Offer Details</p>
                                    <ul className="list-disc list-inside text-grey-5 text-xs space-y-1">
                                      {getOfferDetailsContent(selectedOfferDetails).map((detail, index) => (
                                        <li key={index} className="text-grey-5 text-xs font-normal">
                                          {detail}
                                        </li>
                                      ))}
                                      <li className="text-grey-5 text-xs font-normal">
                                        {selectedOfferDetails ? getOfferActiveDate(selectedOfferDetails) : 'Unknown date'}
                                      </li>
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="flex w-6 h-6 items-center justify-center gap-2 p-1 absolute top-3 right-3 bg-gray-0 rounded-md border border-solid border-gray-2"
                              onClick={(e) => handleDropdownToggle(e)}
                            >
                              <FontAwesomeIcon icon={faRefresh} className="w-3 h-3 text-grey-5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Change offer</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Button
                  variant="outline"
                  className="flex h-14 items-center justify-center gap-2 px-4 py-[5px] w-full bg-grey-1 rounded-xl border border-dashed border-grey-5 hover:bg-grey-2"
                  onClick={(e) => handleDropdownToggle(e)}
                >
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faPlus} className="w-3 h-3 text-grey-5" />
                    <div className="font-medium text-grey-5 text-sm">
                      Select Offer
                    </div>
                  </div>
                </Button>
              )}
            </div>

          </div>

          {validationError && (
            <p className="text-sm text-red-500">{validationError}</p>
          )}
        </div>

        {/* Select Payout */}
        {selectedOffer && (
          <div className="flex flex-col items-start relative bg-gray-0 rounded-xl">
            <div className="flex items-center gap-1 pt-0 pb-2 px-0 relative self-stretch w-full flex-[0_0_auto]">
              <Label className="relative w-fit mt-[-1.00px] font-medium-75-labels font-[number:var(--medium-75-labels-font-weight)] text-gray-9 text-[length:var(--medium-75-labels-font-size)] tracking-[var(--medium-75-labels-letter-spacing)] leading-[var(--medium-75-labels-line-height)] whitespace-nowrap [font-style:var(--medium-75-labels-font-style)]">
                Select Payout
              </Label>
            </div>
            <Select
              value={isAutomatedPayout ? 'automated' : (selectedPayoutId?.toString() || '')}
              onValueChange={handlePayoutSelect}
            >
              <SelectTrigger className="flex items-center gap-1 px-3 py-[5px] relative self-stretch w-full flex-[0_0_auto] bg-gray-0 rounded-lg border border-solid border-gray-2 focus:outline-none focus:ring-0">
                <SelectValue
                  className="relative flex-1 mt-[-1.00px] font-regular-100-paragraph font-[number:var(--regular-100-paragraph-font-weight)] text-gray-6 text-[length:var(--regular-100-paragraph-font-size)] tracking-[var(--regular-100-paragraph-letter-spacing)] leading-[var(--regular-100-paragraph-line-height)] [font-style:var(--regular-100-paragraph-font-style)]"
                  placeholder="Select a payout type"
                />
              </SelectTrigger>
              <SelectContent>
                {/* Show Automated Payout option if available */}
                {isAutomatedPayoutAvailable && (
                  <SelectItem value="automated">
                    Automated Payout
                  </SelectItem>
                )}

                {/* Show regular payouts */}
                {availablePayouts.length === 0 && !isAutomatedPayoutAvailable ? (
                  <div className="p-2 text-sm text-gray-500">
                    No payouts available
                  </div>
                ) : (
                  availablePayouts.map((payout) => (
                    <SelectItem key={payout.id} value={payout.id.toString()}>
                      {getPayoutLabel(payout) || payout.label || payout.payoutType}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Email notification section */}
        <div className="relative">
          <SendEmailNotificationForm
            selectedNode={selectedNode}
            actionOptions={combinedActionOptions}
            onUpdateMessageTemplate={onUpdateMessageTemplate}
            resetMessageTemplate={resetMessageTemplate}
            sendEmail={sendEmail}
            setSendEmail={updateSendEmailState}
            onEmailMetadataUpdate={handleEmailMetadataUpdate}
            onValidationChange={handleEmailValidationChange}
          />
          {emailValidationError && sendEmail && (
            <p className="text-sm text-red-500 mt-2">{emailValidationError}</p>
          )}
        </div>
      </div>

      {/* Action buttons - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-start p-4 border-t bg-white shadow-lg z-50">
        <Button variant="outline" onClick={handleSave} className="bg-white">
          Done
        </Button>
      </div>

      {/* Sheet-compatible dropdown */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-[9999]"
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            // Only close if clicking outside the dropdown content
            if (e.target === e.currentTarget) {
              setIsDropdownOpen(false);
            }
          }}
        >
          <div
            className="absolute bg-white rounded-2xl shadow-[0px_8px_24px_8px_rgba(93,105,116,0.05),0px_4px_8px_0px_rgba(93,105,116,0.08),0px_2px_6px_-4px_rgba(0,0,0,0.12)] overflow-hidden offer-dropdown-portal"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 10000,
            }}
          >
            <div className="p-2 space-y-2">
              {/* Search input */}
              <div className="flex items-center gap-2 px-2 h-8 rounded-lg border border-solid border-[#E5E8EB] bg-white">
                <Search className="w-4 h-4 text-[#A2ADB9] flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search Offer"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 text-grey-6 text-sm border-0 outline-none bg-transparent placeholder:text-[#A2ADB9] h-8"
                  autoFocus
                  onFocus={(e) => e.target.select()}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>

              {/* Offers list with scroll */}
              <div className="max-h-[350px] overflow-y-auto">
                {filteredOffers.length === 0 ? (
                  <div className="p-4 text-center text-[#A2ADB9] text-sm">
                    {searchTerm ? 'No active offers found matching your search' : 'No active offers available'}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredOffers.map((offer) => {
                      const iconData = getOfferIconData(offer);
                      const isDisabled = offer.expired || offer.promos?.[0]?.status === OFFER_STATUS.PAUSED || offer.links?.[0]?.status === OFFER_STATUS.PAUSED;
                      const disabledReason = offer.expired
                        ? 'This offer has expired'
                        : offer.promos?.[0]?.status === OFFER_STATUS.PAUSED
                          ? 'This promo code offer is paused'
                          : offer.links?.[0]?.status === OFFER_STATUS.PAUSED
                            ? 'This link offer is paused'
                            : '';

                      if (isDisabled) {
                        return (
                          <TooltipProvider key={offer.id}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2 px-2 py-2 w-full rounded-lg h-auto justify-start opacity-50 cursor-not-allowed bg-gray-100 hover:bg-gray-100">
                                  <div className={`inline-flex items-start p-[3px] ${iconData.bgColor} rounded-lg flex-shrink-0 opacity-60`}>
                                    <div className="flex w-[18px] h-[18px] items-center justify-center">
                                      {iconData.iconType === 'link' ? (
                                        <FontAwesomeIcon icon={faLink} className="w-[14px] h-[14px] text-white" />
                                      ) : (
                                        <ShoppingCart className="w-[14px] h-[14px] text-white" />
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-1 text-left">
                                    <div className="font-medium text-grey-4 text-xs leading-5 truncate">
                                      {offer.name}
                                    </div>
                                  </div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-[10001]">
                                {disabledReason}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      }

                      return (
                        <Button
                          key={offer.id}
                          variant="ghost"
                          className={`flex items-center gap-2 px-2 py-2 w-full rounded-lg h-auto justify-start ${offer.id === selectedOfferId ? 'bg-[#E5E8EB]' : 'hover:bg-[#E5E8EB]'}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOfferSelect(offer.id, e);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <div className={`inline-flex items-start p-[3px] ${iconData.bgColor} rounded-lg flex-shrink-0`}>
                            <div className="flex w-[18px] h-[18px] items-center justify-center">
                              {iconData.iconType === 'link' ? (
                                <FontAwesomeIcon icon={faLink} className="w-[14px] h-[14px] text-white" />
                              ) : (
                                <ShoppingCart className="w-[14px] h-[14px] text-white" />
                              )}
                            </div>
                          </div>
                          <div className="flex-1 text-left">
                            <div className="font-medium text-grey-5 text-xs leading-5 truncate">
                              {offer.name}
                            </div>
                          </div>
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
